import { zodResolver } from "@hookform/resolvers/zod";
import { HTTPError } from "@marketplace/http";
import { notifications } from "@marketplace/ui/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm, type UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { CreateProductAutomationRequestDto } from "../../dtos/create-product-automation-request-dto";
import { ProductQueryKeys } from "../../generators/product-query-keys";
import { usePostCreateProductAutomationMutation } from "../mutations/use-post-create-product-automation-mutation";
import { usePutUpdateProductAutomationMutation } from "../mutations/use-put-update-product-automation-mutation";
import { useGetProductAutomationDetailQuery } from "../queries/use-get-product-automation-detail-query";
import {
  type ProductAutomationFormSchema,
  useProductAutomationFormSchema
} from "../schemas/use-product-automation-form-schema";

type UseProductAutomationFormProps = {
  productId: string;
  isAutomationActive: boolean;
  onSuccess?: () => void;
  automationError?: Error | null;
};

type UseProductAutomationFormReturn = {
  formMethods: UseFormReturn<ProductAutomationFormSchema>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  isPending: boolean;
};

export const useProductAutomationForm = ({
  productId,
  isAutomationActive,
  onSuccess,
  automationError
}: UseProductAutomationFormProps): UseProductAutomationFormReturn => {
  const { t } = useTranslation("product");
  const queryClient = useQueryClient();
  const schema = useProductAutomationFormSchema();

  const formMethods = useForm<ProductAutomationFormSchema>({
    resolver: zodResolver(schema),
    defaultValues: {
      link_endpoint: "",
      authentification: "",
      body_fields: [{ title: "", key: "" }],
      response_fields: [{ key: "" }]
    }
  });

  // Fetch automation detail if editing
  const { data: automationData, isLoading: isLoadingAutomation } =
    useGetProductAutomationDetailQuery({
      productId,
      options: {
        enabled: isAutomationActive && !!productId
      }
    });

  // Populate form with fetched data
  useEffect(() => {
    if (automationData?.data && isAutomationActive) {
      const { product_automation, product_automation_field } =
        automationData.data;

      // Separate payload and response fields
      const bodyFields = product_automation_field
        .filter((field) => field.type === "payload")
        .map((field) => ({
          id: field.id,
          title: field.title,
          key: field.key
        }));

      const responseFields = product_automation_field
        .filter((field) => field.type === "response")
        .map((field) => ({
          id: field.id,
          key: field.key
        }));

      formMethods.reset({
        link_endpoint: product_automation.link_endpoint,
        authentification: product_automation.authentification,
        body_fields:
          bodyFields.length > 0 ? bodyFields : [{ title: "", key: "" }],
        response_fields:
          responseFields.length > 0 ? responseFields : [{ key: "" }]
      });
    }
  }, [automationData, isAutomationActive, formMethods]);

  // Clear form when automation doesn't exist (404 error)
  useEffect(() => {
    if (automationError && automationError instanceof HTTPError) {
      if (automationError.response.status === 404) {
        formMethods.reset({
          link_endpoint: "",
          authentification: "",
          body_fields: [{ title: "", key: "" }],
          response_fields: [{ key: "" }]
        });
      }
    }
  }, [automationError, formMethods]);

  const createMutation = usePostCreateProductAutomationMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t("automationModal.notifications.successCreate")
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t("automationModal.notifications.failedToCreate")
          });
        }
      }
    }
  });

  const updateMutation = usePutUpdateProductAutomationMutation({
    options: {
      onSuccess: () => {
        notifications.show({
          message: t("automationModal.notifications.successUpdate")
        });
        queryClient.invalidateQueries({
          queryKey: ProductQueryKeys._def
        });
        formMethods.reset();
        onSuccess?.();
      },
      onError: (error) => {
        if (error instanceof HTTPError) {
          notifications.show({
            variant: "error",
            message: t("automationModal.notifications.failedToUpdate")
          });
        }
      }
    }
  });

  const onSubmit = formMethods.handleSubmit(async (data) => {
    const payload: CreateProductAutomationRequestDto = {
      product_id: productId,
      product_automation: {
        link_endpoint: data.link_endpoint,
        authentification: data.authentification
      },
      product_automation_field: [
        ...data.body_fields.map((field) => ({
          ...(field.id && { id: field.id }),
          title: field.title,
          key: field.key,
          type: "payload" as const
        })),
        ...data.response_fields.map((field) => ({
          ...(field.id && { id: field.id }),
          title: field.key,
          key: field.key,
          type: "response" as const
        }))
      ]
    };

    if (isAutomationActive) {
      await updateMutation.mutateAsync(payload);
    } else {
      await createMutation.mutateAsync(payload);
    }
  });

  return {
    formMethods,
    onSubmit,
    isPending:
      createMutation.isPending ||
      updateMutation.isPending ||
      isLoadingAutomation
  };
};
